import { effect, signal } from '@angular/core';
import { ColumnState, GridApi, GridOptions } from 'ag-grid-community';

export class AgGirdColumnStateManager {
  constructor(
    private readonly gridOptions: GridOptions,
    private storageKey: string,
  ) {
    effect(() => {
      const loaded = this.initialRowsLoaded();

      if (loaded) {
        const pagination = this.getPaginationFromStorage();

        setTimeout(() => {
          if (pagination?.currentPage) {
            this.gridApi()?.setGridOption('paginationPageSize', pagination.pageSize);
            this.gridApi()?.paginationGoToPage(pagination.currentPage);
          }
        }, 50);
      }
    });
  }

  readonly gridReady = signal(false);
  readonly initialRowsLoaded = signal(false);

  readonly gridApi = signal<GridApi | null>(null);

  saveGridState() {
    if (!this.gridReady() || !this.initialRowsLoaded()) {
      return;
    }

    const gridState: GridState = {
      columnState: this.gridApi()?.getColumnState(),
      filterModel: this.gridApi()?.getFilterModel(),
      pagination: {
        currentPage: this.gridApi()?.paginationGetCurrentPage(),
        pageSize: this.gridApi()?.paginationGetPageSize(),
      },
    };

    localStorage.setItem(this.storageKey, JSON.stringify(gridState));
  }

  loadGridState(): void {
    const gridState = localStorage.getItem(this.storageKey);
    if (!gridState) {
      return;
    }

    try {
      const state = JSON.parse(gridState) as GridState;

      if (state.columnState) {
        this.gridApi()?.applyColumnState({ state: state.columnState });
      }

      if (state.filterModel) {
        this.gridApi()?.setFilterModel(state.filterModel);
      }
    } catch (e) {
      console.error('Failed to load grid state', e);
      localStorage.removeItem(this.storageKey);
    }
  }

  getPaginationFromStorage() {
    const gridState = localStorage.getItem(this.storageKey);
    if (!gridState) {
      return null;
    }

    try {
      const state = JSON.parse(gridState) as GridState;

      return state.pagination;
    } catch (e) {
      console.error('Failed to load grid state', e);
      localStorage.removeItem(this.storageKey);

      return null;
    }
  }

  overrideStorageKey(key: string) {
    this.storageKey = key;
  }
}

interface GridState {
  columnState?: ColumnState[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterModel: Record<string, any> | undefined;
  pagination: {
    currentPage: number | undefined;
    pageSize: number | undefined;
  };
}
